import {
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Options } from 'focus-trap';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import * as URI from 'urijs';

import { State } from '®app/ngrx';
import { LIST_ID } from '®shared/container-structure/common';
import { getItemMenuButtonSelector } from '®shared/focus';
import IsbnUtils from '®shared/isbns/isbn-utils';

import {
  IsbnOrDoi,
  RequestDigitisationIdentifier,
} from './request-digitisation-identifier';
import {
  HideRequestDigitisationModalAction,
  RequestDigitisationFinishedReason,
  SelectRequestDigitisationIdentifierAction,
} from './request-digitisation.actions';
import { RequestDigitisationService } from './request-digitisation.service';
import {
  RequestDigitisationState,
  selectRequestDigitisationState,
} from './request-digitisation.state';

@Component({
  selector: 'rl-request-digitisation-modal',
  templateUrl: './request-digitisation-modal.component.html',
})
export class RequestDigitisationModalComponent implements OnDestroy, OnInit {
  @ViewChild('requestDigitisationModal', { static: true })
  public requestDigitisationModal: ModalDirective;

  state: RequestDigitisationState;
  identifierType?: IsbnOrDoi;
  identifiers?: string[];
  requestDigitisationUrl?: string | null;

  selectIdentifierForm: FormGroup;
  selectedIdentifier: string | null;

  private stateSubscription: Subscription;
  private postMessageSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    @Inject(LIST_ID) public listId: string,
    @Inject(LOCALE_ID) private locale: string,
    private service: RequestDigitisationService,
    private store: Store<State>,
  ) {
    this.createForm();
  }

  private localiseRequestDigitisationUrl(
    requestDigitisationUrl: string | null,
  ): string | null {
    if (requestDigitisationUrl) {
      const requestUrl = new URI(requestDigitisationUrl);
      requestUrl.setSearch('lang', this.locale);
      return requestUrl.toString();
    }
    return null;
  }

  createForm() {
    this.selectIdentifierForm = this.formBuilder.group({
      selectedIdentifier: this.formBuilder.control(this.selectedIdentifier, [
        Validators.required,
      ]),
    });
  }

  rebuildForm() {
    this.selectIdentifierForm.reset({
      selectedIdentifier: this.selectedIdentifier || null,
    });
  }

  ngOnDestroy(): void {
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
    }
    if (this.postMessageSubscription) {
      this.postMessageSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.stateSubscription = this.store
      .select(selectRequestDigitisationState)
      .subscribe(state => {
        this.state = state;
        this.requestDigitisationUrl = this.localiseRequestDigitisationUrl(
          this.state.requestDigitisationUrl,
        );

        const { isShown } = this.state.modalControls;
        if (isShown && !this.requestDigitisationModal.isShown) {
          this.requestDigitisationModal.show();
        } else if (!isShown && this.requestDigitisationModal.isShown) {
          this.requestDigitisationModal.hide();
        }

        if (!this.requestDigitisationUrl) {
          const { resource = null } = this.state.currentItem || {};
          if (resource && resource.isBookOrChapterResource()) {
            this.identifierType = IsbnOrDoi.ISBN;
            this.identifiers = IsbnUtils.isbnsToUniqueIsbn13s(resource.isbns);
          } else if (resource && resource.isArticleOrJournalResource()) {
            this.identifierType = IsbnOrDoi.DOI;
            this.identifiers = resource.dois;
          }
        }
      });
  }

  get focusTrapConfig(): Options {
    if (!this.state.currentItem) {
      return {};
    }

    const setReturnFocus = getItemMenuButtonSelector(this.state.currentItem.id);
    return { setReturnFocus };
  }

  onRequestDigitisationShown() {
    this.subscribeToPostMessage();
  }

  onRequestDigitisationHidden(modal: ModalDirective) {
    if (this.postMessageSubscription) {
      this.postMessageSubscription.unsubscribe();
    }
    if (
      modal.dismissReason === 'esc' ||
      modal.dismissReason === 'backdrop-click'
    ) {
      this.onCancel();
    }

    this.rebuildForm();
  }

  onCancel() {
    this.store.dispatch(
      new HideRequestDigitisationModalAction({
        reason: RequestDigitisationFinishedReason.CANCELLED,
        item: this.state.currentItem,
        listId: this.listId,
      }),
    );

    this.rebuildForm();
  }

  onSelectIdentifier() {
    const formModel = this.selectIdentifierForm.value;
    this.store.dispatch(
      new SelectRequestDigitisationIdentifierAction({
        selectedIdentifier: {
          value: formModel.selectedIdentifier,
          type: this.identifierType,
        } as RequestDigitisationIdentifier,
        item: this.state.currentItem,
        user: this.state.user,
      }),
    );
  }

  subscribeToPostMessage() {
    if (this.postMessageSubscription) {
      this.postMessageSubscription.unsubscribe();
    }

    this.postMessageSubscription = this.service
      .getRequestDigitisationFinishedReason()
      .subscribe((reason: RequestDigitisationFinishedReason) => {
        this.store.dispatch(
          new HideRequestDigitisationModalAction({
            reason,
            item: this.state.currentItem,
            listId: this.listId,
          }),
        );

        this.rebuildForm();
      });
  }
}
